import React, { useState, useEffect } from "react";
import axios from "axios";

import { Container, Button, Form, Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { TUTOR_PROFILE_SETTINGS_API } from "../../../api/api";
import { toast } from "react-toastify";

import { FaEye, FaEyeSlash } from "react-icons/fa";

import { handleApiError } from "../../../utils/apiErrorHandler";

import "./TutorSettings.css";

const TutorSettings = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("security");
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Payment state
  const [paymentData, setPaymentData] = useState({
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    ifsc: "",
    upiId: "",
    taxId: "",
    billingAddress: "",
  });

  // Original payment data to track changes
  const [originalPaymentData, setOriginalPaymentData] = useState(paymentData);

  // Notification state
  const [notificationData, setNotificationData] = useState({
    email: {
      assignmentSubmissions: true,
      studentQueries: true,
      paymentUpdates: true,
      studentFeedback: true,
    },
    push: {
      assignmentSubmissions: true,
      studentQueries: false,
      paymentUpdates: false,
      studentFeedback: true,
    },
  });

  //loading
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("user_id");

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  // Handle input change for password
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle input change for payment
  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleShow = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Handle change for Notification
  const handleNotificationToggle = (type, field) => {
    setNotificationData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: !prev[type][field],
      },
    }));
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      navigate("/signin");

      return;
    }

    const fetchPaymentDetails = async () => {
      try {
        const response = await axios.post(
          TUTOR_PROFILE_SETTINGS_API.GET_TUTOR_BANK_DETAILS,
          { user_id: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200 && data.data) {
          const fetchedData = {
            accountHolderName: data.data.account_holder_name || "",
            bankName: data.data.bank_name || "",
            accountNumber: data.data.account_number || "",
            ifsc: data.data.ifsc_code || "",
            upiId: data.data.upi_id || "",
            taxId: data.data.pan_number || "",
            billingAddress: data.data.billing_address || "",
          };

          setPaymentData(fetchedData);
          setOriginalPaymentData(fetchedData);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching payment details:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/signin");
      }
    };

    fetchPaymentDetails();
  }, [navigate]);

  // Check if payment data changed
  const isPaymentChanged = () => {
    return JSON.stringify(paymentData) !== JSON.stringify(originalPaymentData);
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    setShowPassword({
      current: false,
      new: false,
      confirm: false,
    });
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (!userId || !token) {
      setIsPasswordLoading(false);
      navigate("/signin");

      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showErrorToast("New password and confirm password do not match!");
      return;
    }

    const passwordPayload = {
      user_id: userId,
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
      confirmNewPassword: passwordData.confirmPassword,
    };

    setIsPasswordLoading(true);

    try {
      const response = await axios.post(
        TUTOR_PROFILE_SETTINGS_API.TUTOR_CHANGE_PASSWORD,
        passwordPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        handlePasswordCancel();

        localStorage.clear();
        setTimeout(() => {
          navigate("/signin");
        }, 2000);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error updating password:", error);
      // Server responded with non-2xx status (like 400, 500)

      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handlePaymentCancel = () => {
    setPaymentData(originalPaymentData);
  };

  const handlePaymentSave = async () => {
    if (
      !paymentData.accountHolderName ||
      !paymentData.bankName ||
      !paymentData.accountNumber ||
      !paymentData.ifsc ||
      !paymentData.taxId ||
      !paymentData.billingAddress
    ) {
      showErrorToast("Please fill all required fields");
      return;
    }

    if (!userId || !token) {
      setIsPaymentLoading(false);
      navigate("/signin");

      return;
    }

    const paymentPayload = {
      user_id: userId,
      account_holder_name: paymentData.accountHolderName,
      bank_name: paymentData.bankName,
      account_number: paymentData.accountNumber,
      ifsc_code: paymentData.ifsc,
      upi_id: paymentData.upiId || "",
      pan_number: paymentData.taxId,
      billing_address: paymentData.billingAddress,
    };

    setIsPaymentLoading(true);

    try {
      const response = await axios.post(
        TUTOR_PROFILE_SETTINGS_API.UPDATE_TUTOR_BANK_DETAILS,
        paymentPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });
        setOriginalPaymentData(paymentData);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error updating payment details:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const handleNotificationSave = () => {
    // console.log("Notification preferences saved:", notificationData);
    toast.success("Notification preferences updated!", {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  return (
    <Container className="tutor-settings-container">
      <h4 className="tutor-settings-title">Settings</h4>
      <p className="tutor-settings-subtitle">
        Manage your security, and payment information and notifications
      </p>

      <div className="tutor-settings-tabs-row">
        <Button
          className={`tutor-settings-tab ${
            activeTab === "security" ? "active" : ""
          }`}
          onClick={() => setActiveTab("security")}
        >
          Security
        </Button>

        <Button
          className={`tutor-settings-tab ${
            activeTab === "payment" ? "active" : ""
          }`}
          onClick={() => setActiveTab("payment")}
        >
          Payment
        </Button>

        <Button
          className={`tutor-settings-tab ${
            activeTab === "notifications" ? "active" : ""
          }`}
          onClick={() => setActiveTab("notifications")}
        >
          Notifications
        </Button>
      </div>

      {/* CONTENT CARD */}
      <div
        className={`tutor-settings-card ${
          activeTab === "notifications" ? "no-border-padding" : ""
        }`}
      >
        {/* SECURITY TAB */}
        {activeTab === "security" && (
          <>
            <h5 className="tutor-settings-card-title">Change Password</h5>
            <p className="tutor-settings-card-subtitle">
              Update your password to keep your account secure
            </p>

            <Form onSubmit={handlePasswordUpdate}>
              {/* CURRENT PASSWORD */}
              <Form.Group className="tutor-settings-form-group">
                <Form.Label className="tutor-settings-form-label">
                  Current Password *
                </Form.Label>
                <div className="tutor-settings-password-input-wrapper">
                  <Form.Control
                    type={showPassword.current ? "text" : "password"}
                    name="currentPassword"
                    placeholder="Enter current password"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className={`tutor-settings-input ${
                      passwordData.currentPassword ? "filled" : ""
                    }`}
                    required
                  />
                  <span
                    className="tutor-settings-password-toggle-icon"
                    onClick={() => toggleShow("current")}
                  >
                    {showPassword.current ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </Form.Group>

              {/* NEW PASSWORD */}
              <Form.Group className="tutor-settings-form-group">
                <Form.Label className="tutor-settings-form-label">
                  New Password *
                </Form.Label>
                <div className="tutor-settings-password-input-wrapper">
                  <Form.Control
                    type={showPassword.new ? "text" : "password"}
                    name="newPassword"
                    placeholder="Enter new password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className={`tutor-settings-input ${
                      passwordData.newPassword ? "filled" : ""
                    }`}
                    required
                    pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}"
                    title="Min 8 chars with uppercase, lowercase, number & symbol"
                  />
                  <span
                    className="tutor-settings-password-toggle-icon"
                    onClick={() => toggleShow("new")}
                  >
                    {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </Form.Group>

              {/* CONFIRM PASSWORD */}
              <Form.Group className="tutor-settings-form-group">
                <Form.Label className="tutor-settings-form-label">
                  Confirm New Password *
                </Form.Label>
                <div className="tutor-settings-password-input-wrapper">
                  <Form.Control
                    type={showPassword.confirm ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Re-enter new password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className={`tutor-settings-input ${
                      passwordData.confirmPassword ? "filled" : ""
                    }`}
                    required
                    pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}"
                    title="Min 8 chars with uppercase, lowercase, number & symbol"
                  />
                  <span
                    className="tutor-settings-password-toggle-icon"
                    onClick={() => toggleShow("confirm")}
                  >
                    {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </Form.Group>

              {/* FOOTER BUTTONS */}
              <div className="tutor-settings-footer">
                <Button
                  type="button"
                  className="tutor-settings-cancel-btn"
                  onClick={handlePasswordCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="tutor-settings-update-btn"
                  disabled={isPasswordLoading}
                >
                  {isPasswordLoading ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </Form>
          </>
        )}

        {/* PAYMENT TAB */}
        {activeTab === "payment" && (
          <>
            <h5 className="tutor-settings-card-title">Bank Account Details</h5>
            <p className="tutor-settings-card-subtitle">
              Configure your payment information for receiving earnings
            </p>

            <Form>
              <Row>
                <Form.Group
                  as={Col}
                  md={6}
                  controlId="accountHolderName"
                  className="tutor-settings-payment-form-group"
                >
                  <Form.Label className="tutor-settings-form-label">
                    Account Holder Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="accountHolderName"
                    value={paymentData.accountHolderName}
                    onChange={handlePaymentChange}
                    placeholder="Enter account holder name"
                    className={`tutor-settings-input ${
                      paymentData.accountHolderName ? "filled" : ""
                    }`}
                  />
                </Form.Group>

                <Form.Group
                  as={Col}
                  md={6}
                  controlId="bankName"
                  className="tutor-settings-payment-form-group"
                >
                  <Form.Label className="tutor-settings-form-label">
                    Bank Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="bankName"
                    value={paymentData.bankName}
                    onChange={handlePaymentChange}
                    placeholder="Enter bank name"
                    className={`tutor-settings-input ${
                      paymentData.bankName ? "filled" : ""
                    }`}
                  />
                </Form.Group>
              </Row>

              <Row>
                <Form.Group
                  as={Col}
                  md={6}
                  controlId="accountNumber"
                  className="tutor-settings-payment-form-group"
                >
                  <Form.Label className="tutor-settings-form-label">
                    Account Number *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="accountNumber"
                    value={paymentData.accountNumber}
                    onChange={handlePaymentChange}
                    placeholder="Enter account number"
                    className={`tutor-settings-input ${
                      paymentData.accountNumber ? "filled" : ""
                    }`}
                  />
                </Form.Group>

                <Form.Group
                  as={Col}
                  md={6}
                  controlId="ifsc"
                  className="tutor-settings-payment-form-group"
                >
                  <Form.Label className="tutor-settings-form-label">
                    IFSC / Routing Number *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="ifsc"
                    value={paymentData.ifsc}
                    onChange={handlePaymentChange}
                    placeholder="Enter IFSC or routing number"
                    className={`tutor-settings-input ${
                      paymentData.ifsc ? "filled" : ""
                    }`}
                  />
                </Form.Group>
              </Row>

              <Row>
                <Form.Group
                  as={Col}
                  md={6}
                  controlId="upiId"
                  className="tutor-settings-payment-form-group"
                >
                  <Form.Label className="tutor-settings-form-label">
                    UPI ID (Optional)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="upiId"
                    value={paymentData.upiId}
                    onChange={handlePaymentChange}
                    placeholder="Enter UPI ID"
                    className={`tutor-settings-input ${
                      paymentData.upiId ? "filled" : ""
                    }`}
                  />
                </Form.Group>

                <Form.Group
                  as={Col}
                  md={6}
                  controlId="taxId"
                  className="tutor-settings-payment-form-group"
                >
                  <Form.Label className="tutor-settings-form-label">
                    Tax ID / PAN Number *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="taxId"
                    value={paymentData.taxId}
                    onChange={handlePaymentChange}
                    placeholder="Enter tax ID or PAN number"
                    className={`tutor-settings-input ${
                      paymentData.taxId ? "filled" : ""
                    }`}
                  />
                </Form.Group>
              </Row>

              <Form.Group
                controlId="billingAddress"
                className="tutor-settings-payment-form-group mb-3"
              >
                <Form.Label className="tutor-settings-form-label">
                  Billing Address
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="billingAddress"
                  value={paymentData.billingAddress}
                  onChange={handlePaymentChange}
                  placeholder="Enter billing address"
                  className={`tutor-payment-textarea ${
                    paymentData.billingAddress ? "filled" : ""
                  }`}
                />
              </Form.Group>

              <div className="tutor-settings-footer">
                {isPaymentChanged() && (
                  <Button
                    type="button"
                    onClick={handlePaymentCancel}
                    className="tutor-payment-cancel-btn"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={handlePaymentSave}
                  className="tutor-payment-save-btn"
                  disabled={isPaymentLoading}
                >
                  {isPaymentLoading ? "Saving..." : "Save Payment Details"}
                </Button>
              </div>
            </Form>
          </>
        )}

        {/* NOTIFICATIONS TAB */}
        {activeTab === "notifications" && (
          <>
            {/* EMAIL NOTIFICATIONS */}
            <div className="tutor-settings-notification-card">
              <h6 className="tutor-settings-notification-section-title">
                Email Notifications
              </h6>
              <p className="tutor-settings-notification-section-subtitle">
                Choose what email updates you want to receive
              </p>

              <div className="tutor-settings-notification-row">
                <div>
                  <p className="tutor-settings-notification-title">
                    Assignment Submissions
                  </p>
                  <span className="tutor-settings-notification-description">
                    Get notified when students submit assignments
                  </span>
                </div>

                <Form.Check
                  type="switch"
                  className="tutor-settings-notification-toggle"
                  checked={notificationData.email.assignmentSubmissions}
                  onChange={() =>
                    handleNotificationToggle("email", "assignmentSubmissions")
                  }
                />
              </div>

              <div className="tutor-settings-notification-row">
                <div>
                  <p className="tutor-settings-notification-title">
                    Student Queries
                  </p>
                  <span className="tutor-settings-notification-description">
                    Receive emails for new student questions
                  </span>
                </div>

                <Form.Check
                  type="switch"
                  className="tutor-settings-notification-toggle"
                  checked={notificationData.email.studentQueries}
                  onChange={() =>
                    handleNotificationToggle("email", "studentQueries")
                  }
                />
              </div>

              <div className="tutor-settings-notification-row">
                <div>
                  <p className="tutor-settings-notification-title">
                    Payment Updates
                  </p>
                  <span className="tutor-settings-notification-description">
                    Get notified about payments and payouts
                  </span>
                </div>

                <Form.Check
                  type="switch"
                  className="tutor-settings-notification-toggle"
                  checked={notificationData.email.paymentUpdates}
                  onChange={() =>
                    handleNotificationToggle("email", "paymentUpdates")
                  }
                />
              </div>

              <div className="tutor-settings-notification-row last">
                <div>
                  <p className="tutor-settings-notification-title">
                    Student Feedback
                  </p>
                  <span className="tutor-settings-notification-description">
                    Receive emails when students leave feedback
                  </span>
                </div>

                <Form.Check
                  type="switch"
                  className="tutor-settings-notification-toggle"
                  checked={notificationData.email.studentFeedback}
                  onChange={() =>
                    handleNotificationToggle("email", "studentFeedback")
                  }
                />
              </div>
            </div>

            {/* PUSH NOTIFICATIONS */}
            <div className="tutor-settings-notification-card">
              <h6 className="tutor-settings-notification-section-title">
                Push Notifications
              </h6>
              <p className="tutor-settings-notification-section-subtitle">
                Manage browser and mobile push notifications
              </p>

              <div className="tutor-settings-notification-row">
                <div>
                  <p className="tutor-settings-notification-title">
                    Assignment Submissions
                  </p>
                  <span className="tutor-settings-notification-description">
                    Get notified when students submit assignments
                  </span>
                </div>

                <Form.Check
                  type="switch"
                  className="tutor-settings-notification-toggle"
                  checked={notificationData.push.assignmentSubmissions}
                  onChange={() =>
                    handleNotificationToggle("push", "assignmentSubmissions")
                  }
                />
              </div>

              <div className="tutor-settings-notification-row">
                <div>
                  <p className="tutor-settings-notification-title">
                    Student Queries
                  </p>
                  <span className="tutor-settings-notification-description">
                    Receive alerts for new student questions
                  </span>
                </div>

                <Form.Check
                  type="switch"
                  className="tutor-settings-notification-toggle"
                  checked={notificationData.push.studentQueries}
                  onChange={() =>
                    handleNotificationToggle("push", "studentQueries")
                  }
                />
              </div>

              <div className="tutor-settings-notification-row">
                <div>
                  <p className="tutor-settings-notification-title">
                    Payment Updates
                  </p>
                  <span className="tutor-settings-notification-description">
                    Get notified about payments and payouts
                  </span>
                </div>

                <Form.Check
                  type="switch"
                  className="tutor-settings-notification-toggle"
                  checked={notificationData.push.paymentUpdates}
                  onChange={() =>
                    handleNotificationToggle("push", "paymentUpdates")
                  }
                />
              </div>

              <div className="tutor-settings-notification-row last">
                <div>
                  <p className="tutor-settings-notification-title">
                    Student Feedback
                  </p>
                  <span className="tutor-settings-notification-description">
                    Receive alerts when students leave feedback
                  </span>
                </div>

                <Form.Check
                  type="switch"
                  className="tutor-settings-notification-toggle"
                  checked={notificationData.push.studentFeedback}
                  onChange={() =>
                    handleNotificationToggle("push", "studentFeedback")
                  }
                />
              </div>
            </div>

            <div className="tutor-settings-notification-save-footer">
              <Button
                type="button"
                className="tutor-settings-notification-save-btn"
                onClick={handleNotificationSave}
              >
                Save Preferences
              </Button>
            </div>
          </>
        )}
      </div>
    </Container>
  );
};

export default TutorSettings;
